import { useRef, useState } from 'react';
import { track } from '@generated/hooks/useTrack';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@components/ui/input';
import { createSubscription } from '@generated/server/personal';
import { money, upcomingMonths, PAYMENT_METHODS } from '@generated/components/pricing';
import { PayPanel } from '@generated/components/PayPanel';

export const SUB_PRICE = 30; // yearly print subscription, per paper
const PAPERS = ['Red Hook Star-Revue', 'The Village Star-Revue', 'Both'];

export function Subscribe() {
  const months = upcomingMonths(4).map((m) => m.label);
  const [f, setF] = useState({ name: '', email: '', phone: '', address: '', publication: PAPERS[0] ?? '', startMonth: months[0] ?? '', paymentMethod: 'Zelle', gift: false, giftRecipient: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [payPicked, setPayPicked] = useState(false);
  const orderRef = useRef<HTMLElement | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const amount = f.publication === 'Both' ? 50 : SUB_PRICE;
  const ok = f.name && f.email && f.address;
  const goBack = () => { if (done) setEditId(done); setDone(null); };
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const r = await createSubscription({ data: { ...f, amount, id: editId } });
      if (!editId) track("sub_completed");
      setDone(r?.id ?? '');
    } catch (e) { console.error(e); setError('Sorry, we could not save your subscription. Please try again or call 917-652-9128.'); }
    finally { setSaving(false); }
  };

  if (done !== null) return (
    <section className="mx-auto max-w-2xl text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
      <h2 className="mt-3 font-[family-name:var(--font-heading)] text-3xl font-semibold">Thank you for subscribing!</h2>
      <p className="mt-2 text-sm text-muted-foreground">Your first issue arrives in {f.startMonth}.</p>
      <div className="text-left"><PayPanel method={f.paymentMethod} orderId={`SUB-${done}`} dueNow={amount} total={amount} monthly={false} email={f.email} kind="Subscription" who={f.name || undefined} /></div>
      <p className="mt-6 text-center text-xs text-muted-foreground">Having second thoughts? <button type="button" onClick={goBack} className="underline underline-offset-2 hover:text-foreground">Go back and change your order</button> before paying.</p>
    </section>
  );

  const field = (k: 'name' | 'email' | 'phone', label: string, type = 'text') => (
    <div className="space-y-1.5"><label htmlFor={`s-${k}`} className="text-sm font-medium">{label}</label>
      <Input id={`s-${k}`} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-11 rounded-none bg-background" /></div>
  );
  const chips = (k: 'publication' | 'startMonth' | 'paymentMethod', opts: string[]) => (
    <div className="flex flex-wrap gap-2">{opts.map((o) => (
      <button key={o} type="button" onClick={() => { set(k, o); if (k === 'paymentMethod') { setPayPicked(true); setTimeout(() => orderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); } }} className={`min-h-11 border px-3 text-sm ${f[k] === o ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'}`}>{o}</button>
    ))}</div>
  );

  return (
    <section className="grid gap-8 lg:grid-cols-[1fr_300px]">
      <div className="min-w-0 space-y-5">
        <div className="border-b border-foreground pb-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Delivered by US Mail</div>
          <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold text-[hsl(var(--brand-blue))]">Subscribe for a year</h2>
          <p className="mt-1 text-sm text-muted-foreground">Each issue is mailed to you by US Mail, anywhere in the country.</p>
        </div>
        <div><div className="mb-2 text-sm font-medium">Which paper?</div>{chips('publication', PAPERS)}</div>
        <div className="grid gap-4 sm:grid-cols-2">{field('name', 'Full name')}{field('email', 'Email', 'email')}{field('phone', 'Phone (optional)', 'tel')}</div>
        <div className="space-y-1.5"><label htmlFor="s-addr" className="text-sm font-medium">Mailing address</label>
          <textarea id="s-addr" rows={2} value={f.address} onChange={(e) => set('address', e.target.value)} className="w-full border border-input bg-background p-3 text-sm" /></div>
        <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={f.gift} onChange={(e) => set('gift', e.target.checked)} className="h-4 w-4" />This is a gift</label>
        {f.gift && <div className="space-y-1.5"><label htmlFor="s-gift" className="text-sm font-medium">Gift recipient's name (use their address above)</label>
          <Input id="s-gift" value={f.giftRecipient} onChange={(e) => set('giftRecipient', e.target.value)} className="h-11 rounded-none bg-background" /></div>}
        <div><div className="mb-2 text-sm font-medium">First issue</div>{chips('startMonth', months)}</div>
        <div><div className="mb-2 text-sm font-medium">Payment method</div>{chips('paymentMethod', PAYMENT_METHODS)}
          <p className="mt-2 text-xs text-muted-foreground">{f.paymentMethod.startsWith('Zelle') ? 'After you submit, you\'ll see our Zelle number, the exact amount and your order number to send from your bank app.' : f.paymentMethod === 'PayPal' ? 'After you submit, a "Pay with PayPal" button opens PayPal with your amount filled in.' : 'After you submit, a "Pay by card" button opens our secure Stripe checkout.'}</p>
        </div>
        <div className="space-y-1.5"><label htmlFor="s-notes" className="text-sm font-medium">Delivery notes (optional)</label>
          <textarea id="s-notes" rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} className="w-full border border-input bg-background p-3 text-sm" /></div>
      </div>
      <aside ref={orderRef} className={`h-fit border-2 border-foreground bg-card p-5 transition-all ${payPicked ? 'lg:self-end ring-4 ring-primary/30' : 'lg:sticky lg:top-4'}`}>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">Your subscription</div>
        <div className="mt-2 text-sm">{f.publication} · 12 months</div>
        <div className="mt-3 font-[family-name:var(--font-heading)] text-4xl tabular-nums">{money(amount)}</div>
        <div className="text-xs text-muted-foreground">per year · sent by US Mail</div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button type="button" disabled={!ok || saving} onClick={submit} className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}Subscribe &amp; pay {money(amount)}
        </button>
        {!ok && <p className="mt-2 text-xs text-muted-foreground">Name, email and mailing address are required.</p>}
      </aside>
    </section>
  );
}
